import { Box, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import React, { memo } from 'react';
import BaseTableHead from './BaseTableHead';
import BasePagination from '~/components/jobs/base-pagination/BasePagination';
import styles from './BaseTable.module.scss';
import classNames from 'classnames/bind';
import { SortDirection } from '~/utils/types/common';
import { HeadCelType } from '~/utils/types/common';
import { PAGE_INDEX } from '~/utils/constants/common';
import { useTranslation } from 'react-i18next';

const cx = classNames.bind(styles);

type BaseTableProps<T extends { id?: string }> = {
  data: Array<T>;
  renderCell: (item: T, index: number) => React.ReactElement;
  headCells: Array<HeadCelType>;
  pageIndex?: number;
  pageSize?: number;
  totalItems?: number;
  label?: string;
  searchInput?: string;
  sortBy?: string;
  sortOrder?: SortDirection;
  onChangePage?: (page: number) => void;
  onChangePageSize?: (page: number) => void;
  onRequestSort?: (sortBy: string, sortOrder: SortDirection) => void;
  onRequestSearch?: (searchBy: string, value: string) => void;
};

const BaseTable = <T extends { id: string }>(props: BaseTableProps<T>) => {
  const { t } = useTranslation('admin/common');

  const {
    data,
    headCells,
    totalItems,
    pageIndex,
    pageSize,
    label,
    searchInput,
    sortBy,
    sortOrder,
    renderCell,
    onRequestSort,
    onChangePageSize,
    onRequestSearch,
    onChangePage,
  } = props;

  const handleChangePage = (event: unknown, newPage: number) => {
    onChangePage?.(newPage);
  };

  const handleRequestSort = (sortBy: string, sortOrder: SortDirection) => {
    onRequestSort?.(sortBy, sortOrder);
  };

  const handleRequestSearch = (searchBy: string, value: string) => {
    onRequestSearch?.(searchBy, value);
  };

  const handleChangePageSize = (value: number) => {
    onChangePageSize?.(value);
  };

  return (
    <Box className={cx('table-container')}>
      <TableContainer className='rounded-t-xl border-b-0 bg-white overflow-x-auto'>
        <Table stickyHeader className='min-w-max'>
          <BaseTableHead
            headCells={headCells}
            searchInput={searchInput}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRequestSort={handleRequestSort}
            onRequestSearch={handleRequestSearch}
          />
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headCells.length} align='center' className='border-0 text-base text-[#404854]'>
                  {t('noData', { ns: 'admin/common' })}
                </TableCell>
              </TableRow>
            ) : (
              data.map((item: T, index: number) => {
                return (
                  <TableRow hover role='checkbox' tabIndex={-1} key={item.id} sx={{ cursor: 'pointer' }}>
                    {renderCell(item, index)}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box className={cx('pagination-container')}>
        <BasePagination
          label={label}
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalItems={totalItems}
          count={Math.max(PAGE_INDEX, Math.ceil(Number(totalItems || 0) / (pageSize || PAGE_INDEX)))}
          onChange={handleChangePage}
          onChangePageSize={handleChangePageSize}
        />
      </Box>
    </Box>
  );
};

export default memo(BaseTable) as typeof BaseTable;
